import { useState, type CSSProperties, type FormEvent } from "react";
import {
  Button,
  PasswordInput,
  Text,
  TextInput,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ArrowRight } from "lucide-react";
import { ACCENTS, FONTS, NEUTRALS, type Neutrals } from "../theme/tokens";
import { ForgotPasswordModal } from "./ForgotPasswordModal";

interface Props {
  /** Вызывается после успешного login — родитель пере-fetch'ит /api/me/. */
  onSuccess: () => void;
}

/** Возвращает значение cookie или пустую строку. */
function readCookie(name: string): string {
  const match = document.cookie.match(
    new RegExp("(^| )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]+)"),
  );
  return match ? decodeURIComponent(match[2]) : "";
}

// US-82: запоминаем последний успешный логин в localStorage, чтобы при
// возвращении (в т.ч. по ссылке из письма «на согласование») поле «Логин»
// было заполнено и оставалось дописать только пароль. Храним ТОЛЬКО логин
// (не пароль) — это удобство, а не аутентификация. localStorage может быть
// недоступен (приватный режим / отключён) — все обращения в try/catch.
const LAST_USERNAME_KEY = "skedd_last_username";

function readLastUsername(): string {
  try {
    return localStorage.getItem(LAST_USERNAME_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveLastUsername(value: string): void {
  try {
    if (value) localStorage.setItem(LAST_USERNAME_KEY, value);
  } catch {
    // localStorage недоступен — молча игнорируем, это лишь удобство.
  }
}

/**
 * Полноэкранный split-layout логин (handoff B1, Direction C).
 *
 * Слева — форма (логин + пароль + CTA), справа — брендовая панель с
 * паттерном-календарём и floating-карточкой таймшита. На < 960px
 * правая панель скрывается, форма центрируется.
 *
 * Логика та же, что в US-38: POST `/api/auth/login/`, CSRF из cookie
 * `skedd_csrftoken`. После успеха — `onSuccess()`.
 */
export function LoginScreen({ onSuccess }: Props) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const n: Neutrals =
    theme.other?.neutrals?.[dark ? "dark" : "light"] ?? NEUTRALS[dark ? "dark" : "light"];
  const accent = ACCENTS.indigo;
  const isNarrow = useMediaQuery("(max-width: 960px)");

  // US-82: префилл логина из localStorage. Если он уже есть — фокусируем
  // сразу поле пароля (логин дописывать не нужно).
  const [username, setUsername] = useState(readLastUsername);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const hadSavedUsername = username !== "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/login/", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": readCookie("skedd_csrftoken"),
        },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        let detail = `Ошибка входа (${res.status})`;
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch {
          // ignore — оставим стандартное сообщение
        }
        setError(detail);
        setSubmitting(false);
        return;
      }
      // US-82: запоминаем удачный логин для следующего входа.
      saveLastUsername(username.trim());
      onSuccess();
    } catch (err) {
      setError(`Сетевая ошибка: ${err instanceof Error ? err.message : String(err)}`);
      setSubmitting(false);
    }
  };

  const rootStyle: CSSProperties = {
    width: "100vw",
    minHeight: "100vh",
    background: n.bg,
    color: n.text,
    fontFamily: FONTS.body,
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "1fr 1.05fr",
  };

  return (
    <div style={rootStyle}>
      <LeftPanel
        n={n}
        accent={accent}
        username={username}
        password={password}
        error={error}
        submitting={submitting}
        focusPassword={hadSavedUsername}
        onUsername={setUsername}
        onPassword={setPassword}
        onSubmit={handleSubmit}
      />
      {!isNarrow && <RightPanel accent={accent} dark={dark} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left — form
// ---------------------------------------------------------------------------

interface LeftPanelProps {
  n: Neutrals;
  accent: typeof ACCENTS.indigo;
  username: string;
  password: string;
  error: string | null;
  submitting: boolean;
  /** US-82: если логин уже подставлен из localStorage — фокус сразу на пароль. */
  focusPassword: boolean;
  onUsername: (v: string) => void;
  onPassword: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
}

function LeftPanel({
  n,
  accent,
  username,
  password,
  error,
  submitting,
  focusPassword,
  onUsername,
  onPassword,
  onSubmit,
}: LeftPanelProps) {
  // US-65: state «забыли пароль» modal — локально в LeftPanel где сама кнопка.
  const [forgotOpen, setForgotOpen] = useState(false);
  return (
    <div
      style={{
        padding: "48px 56px",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <SkeddMark size={26} color={accent[500]} />
        <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em" }}>SKEDD</span>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          maxWidth: 380,
          width: "100%",
          marginInline: "auto",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            color: n.text,
          }}
        >
          С возвращением
        </h1>
        <p
          style={{
            margin: "8px 0 32px",
            fontSize: 13.5,
            color: n.textMuted,
            lineHeight: 1.55,
          }}
        >
          Войдите, чтобы заполнить таймшит и&nbsp;посмотреть, что ждёт согласования.
        </p>

        <form onSubmit={onSubmit} data-testid="login-form" noValidate>
          <FieldLabel n={n}>Логин или email</FieldLabel>
          <TextInput
            value={username}
            onChange={(e) => onUsername(e.currentTarget.value)}
            placeholder="логин или рабочая почта"
            autoComplete="username"
            required
            autoFocus={!focusPassword}
            data-testid="login-username"
            mb={14}
            styles={{
              input: {
                fontFamily: FONTS.body,
                fontSize: 14,
                height: 42,
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          />

          <FieldLabel n={n}>Пароль</FieldLabel>
          <PasswordInput
            value={password}
            onChange={(e) => onPassword(e.currentTarget.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            required
            autoFocus={focusPassword}
            data-testid="login-password"
            mb={error ? 12 : 18}
            styles={{
              input: {
                fontFamily: FONTS.body,
                fontSize: 14,
                height: 42,
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          />

          {error && (
            <div
              role="alert"
              data-testid="login-error"
              style={{
                fontSize: 12.5,
                color: "#9a3838",
                background: "#fadcdc",
                border: "1px solid #f1c2c2",
                borderRadius: 6,
                padding: "8px 10px",
                marginBottom: 14,
                lineHeight: 1.4,
              }}
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            loading={submitting}
            fullWidth
            rightSection={!submitting && <ArrowRight size={15} />}
            data-testid="login-submit"
            styles={{
              root: {
                height: 44,
                fontSize: 14,
                fontWeight: 600,
                background: accent[500],
                color: accent.fg,
                boxShadow: "0 1px 2px rgba(91, 107, 224, 0.3)",
              },
              section: { marginInlineStart: 4 },
            }}
          >
            Войти
          </Button>
        </form>

        <Text mt={20} ta="center" style={{ fontSize: 13, lineHeight: 1.5 }}>
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            data-testid="login-forgot-trigger"
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: accent[500],
              fontWeight: 500,
              fontSize: 13,
              textDecoration: "underline",
            }}
          >
            Забыли пароль?
          </button>
        </Text>

        <Text mt={10} ta="center" style={{ fontSize: 12, color: n.textDim, lineHeight: 1.5 }}>
          Не получается войти? Напишите{" "}
          <a
            href="mailto:osviridova7@gmail.com"
            style={{ color: n.textMuted, fontWeight: 500, textDecoration: "none" }}
          >
            Оксане Свиридовой
          </a>
        </Text>
        <ForgotPasswordModal opened={forgotOpen} onClose={() => setForgotOpen(false)} />
      </div>

      <div
        style={{
          fontSize: 11.5,
          color: n.textDim,
          lineHeight: 1.5,
        }}
      >
        © 2026 SKEDD
      </div>
    </div>
  );
}

function FieldLabel({ n, children }: { n: Neutrals; children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        color: n.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontFamily: FONTS.mono,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right — brand panel (gradient + calendar pattern + floating timesheet card)
// ---------------------------------------------------------------------------

interface RightPanelProps {
  accent: typeof ACCENTS.indigo;
  dark: boolean;
}

function RightPanel({ accent, dark }: RightPanelProps) {
  const projects = [
    { color: "#5b6be0", code: "OMNI-24", hours: "16,0" },
    { color: "#3d8b40", code: "PAY-12", hours: "14,5" },
    { color: "#c4831f", code: "INT-07", hours: "9,5" },
  ];

  return (
    <div
      style={{
        // Ревью 21.07: в тёмной теме затемняем брендовую панель ~12%, чтобы
        // она не спорила по насыщенности с тёмной формой слева.
        background: dark
          ? `linear-gradient(140deg, ${accent[600]} 0%, #3f4bb8 100%)`
          : `linear-gradient(140deg, ${accent[500]} 0%, ${accent[600]} 100%)`,
        position: "relative",
        overflow: "hidden",
        padding: 48,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        minHeight: "100vh",
      }}
    >
      <CalendarGridPattern />

      <FloatingTimesheetCard projects={projects} accent={accent} />

      <div style={{ color: "#fff", position: "relative", zIndex: 1 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            opacity: 0.85,
            marginBottom: 8,
            fontFamily: FONTS.mono,
            letterSpacing: "0.02em",
          }}
        >
          — Илья, Senior Developer
        </div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 500,
            lineHeight: 1.35,
            letterSpacing: "-0.01em",
            maxWidth: 420,
          }}
        >
          «За&nbsp;8&nbsp;минут в&nbsp;понедельник закрываю всю прошлую неделю и&nbsp;забываю про
          таймшит до&nbsp;следующей»
        </div>
      </div>
    </div>
  );
}

function CalendarGridPattern() {
  const rows = [0, 1, 2, 3, 4, 5, 6, 7];
  const cols = [0, 1, 2, 3, 4, 5, 6];
  const fills = [
    { x: 20, y: 40, opacity: 0.4 },
    { x: 124, y: 104, opacity: 0.5 },
    { x: 228, y: 168, opacity: 0.35 },
    { x: 72, y: 232, opacity: 0.55 },
    { x: 176, y: 296, opacity: 0.4 },
    { x: 280, y: 360, opacity: 0.45 },
    { x: 20, y: 424, opacity: 0.5 },
  ];
  return (
    <svg
      viewBox="0 0 400 600"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        opacity: 0.18,
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      {rows.map((r) => (
        <g key={r}>
          {cols.map((c) => (
            <rect
              key={c}
              x={20 + c * 52}
              y={40 + r * 64}
              width={44}
              height={52}
              rx={6}
              fill="none"
              stroke="white"
              strokeWidth={1.5}
            />
          ))}
        </g>
      ))}
      {fills.map((f, i) => (
        <rect
          key={i}
          x={f.x}
          y={f.y}
          width={44}
          height={52}
          rx={6}
          fill="white"
          opacity={f.opacity}
        />
      ))}
    </svg>
  );
}

interface CardProps {
  projects: { color: string; code: string; hours: string }[];
  accent: typeof ACCENTS.indigo;
}

function FloatingTimesheetCard({ projects, accent }: CardProps) {
  return (
    <div
      style={{
        position: "absolute",
        top: "24%",
        left: "50%",
        transform: "translate(-50%, 0)",
        width: 320,
        background: "#ffffff",
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 24px 60px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.1)",
        color: "#1f1d18",
        fontFamily: FONTS.body,
        zIndex: 1,
      }}
      aria-hidden="true"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontFamily: FONTS.mono,
            color: "#6a6555",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontWeight: 600,
          }}
        >
          Неделя 17
        </span>
        <span
          style={{
            fontSize: 11,
            fontFamily: FONTS.mono,
            fontWeight: 700,
            padding: "3px 8px",
            borderRadius: 999,
            background: "#dceddd",
            color: "#2f6e32",
          }}
        >
          Согласован
        </span>
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          fontFamily: FONTS.mono,
          marginBottom: 4,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        40,0 / 40 ч
      </div>
      <div
        style={{
          height: 4,
          borderRadius: 999,
          background: "#f0ebdc",
          overflow: "hidden",
          marginBottom: 14,
        }}
      >
        <div style={{ width: "100%", height: "100%", background: accent[500] }} />
      </div>
      {projects.map((p, i) => (
        <div
          key={p.code}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "6px 0",
            borderTop: i === 0 ? "none" : "1px solid #f0ebdc",
            fontSize: 12,
          }}
        >
          <span
            style={{
              width: 3,
              height: 14,
              borderRadius: 1.5,
              background: p.color,
            }}
          />
          <span
            style={{
              fontFamily: FONTS.mono,
              fontSize: 11,
              color: "#6a6555",
            }}
          >
            {p.code}
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: FONTS.mono,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {p.hours} ч
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Brand mark (calendar-style, indigo)
// ---------------------------------------------------------------------------

function SkeddMark({ size = 26, color = "#5b6be0" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x={3} y={5} width={26} height={22} rx={4} stroke={color} strokeWidth={1.5} />
      <path d="M3 11h26" stroke={color} strokeWidth={1.5} />
      <path d="M10 3v4M22 3v4" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
      <path d="M9 17h6M9 21h10" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Logout — same logic as before, exported so App can wire it to the menu.
// ---------------------------------------------------------------------------

export async function performLogout(): Promise<void> {
  try {
    await fetch("/api/auth/logout/", {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRFToken": readCookie("skedd_csrftoken") },
    });
  } finally {
    window.location.href = "/app/";
  }
}
